import { useCallback, useMemo } from 'react';
import { useBadges } from '../context/NotificationProvider';
import { notificationsAPI } from '../services/api';
/**
 * useStaffSidebarBadges — thin adapter over NotificationProvider.
 *
 * Was previously a 45s poller of /notifications + /chat/my-chats. Now every
 * count comes from the shared 5s poll owned by NotificationProvider. The
 * notification LIST is the source of truth for the assignment / announcement /
 * heat splits; the shared `chat` badge covers team chat.
 *
 * Only surfaces for staff role. Returns {} for everyone else.
 */
export default function useStaffSidebarBadges(user) {
  const isStaff = (user?.role || '') === 'staff';
  const { badges: raw, notifications, ackCategory } = useBadges();
  const badges = useMemo(() => {
    if (!isStaff) return {};
    let assignments = 0;
    let announcements = 0;
    let points = 0;
    (notifications || []).forEach((n) => {
      if (n.read) return;
      const t = n.type || n.notification_type || '';
      if (t === 'assignment') assignments += 1;
      else if (t === 'announcement') announcements += 1;
      else if (t === 'heat' || t === 'heat_points') points += Number(n.metadata?.points || 0);
    });
    const chatUnread = Number(raw.chat || 0);
    const next = {};
    if (assignments > 0) next['my-assignments'] = { kind: 'count', value: assignments };
    if (chatUnread > 0) next['staff-team-chat'] = { kind: 'count', value: chatUnread };
    if (announcements > 0) next['staff-announcements'] = { kind: 'count', value: announcements };
    if (points > 0) next['staff-leaderboard'] = { kind: 'points', value: points };
    return next;
  }, [isStaff, notifications, raw.chat]);
  const clearBadge = useCallback(async (itemId) => {
    const BADGE_TYPES = {
      'my-assignments': ['assignment'],
      'staff-announcements': ['announcement'],
      'staff-leaderboard': ['heat', 'heat_points'],
    };
    const types = BADGE_TYPES[itemId];
    if (itemId === 'staff-team-chat') { ackCategory('chat'); return; }
    if (!types) return;
    // Mark matching notification rows read — the badge will drop on the next
    // 5s poll (or sooner via the mark-as-read side effect on the shared list).
    try {
      const res = await notificationsAPI.getAll(500, true).catch(() => null);
      const items = res?.notifications || (Array.isArray(res) ? res : []);
      const targets = items.filter((n) => types.includes(n.type || n.notification_type || ''));
      await Promise.all(targets.map((n) => notificationsAPI.markAsRead(n.id).catch(() => null)));
    } catch { /* non-fatal — next poll re-surfaces truth */ }
  }, [ackCategory]);
  return { badges, clearBadge };
}
